import * as LabelPrimitive from '@radix-ui/react-label';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import * as React from 'react';
import { cn, focusRing } from '../cn.js';

const fieldBase = `w-full rounded-[var(--field-radius)] border border-[var(--field-border)] bg-surface px-3 text-[length:var(--lr-text-base)] text-fg placeholder:text-fg-muted disabled:cursor-not-allowed disabled:opacity-60 aria-[invalid=true]:border-danger ${focusRing}`;

export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({ className, ...props }, ref) => (
  <input ref={ref} className={cn(fieldBase, 'h-[var(--field-height)]', className)} {...props} />
));
Input.displayName = 'Input';

export const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(({ className, ...props }, ref) => (
  <textarea ref={ref} className={cn(fieldBase, 'min-h-20 py-2', className)} {...props} />
));
Textarea.displayName = 'Textarea';

/** Select nativo: acessível por padrão, rápido em formulários densos. */
export const NativeSelect = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(({ className, ...props }, ref) => (
  <select ref={ref} className={cn(fieldBase, 'h-[var(--field-height)] pr-8', className)} {...props} />
));
NativeSelect.displayName = 'NativeSelect';

export const Label = React.forwardRef<React.ElementRef<typeof LabelPrimitive.Root>, React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>>(
  ({ className, ...props }, ref) => (
    <LabelPrimitive.Root ref={ref} className={cn('text-[length:var(--lr-text-sm)] font-medium text-fg', className)} {...props} />
  ),
);
Label.displayName = 'Label';

export const Switch = React.forwardRef<React.ElementRef<typeof SwitchPrimitive.Root>, React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>>(
  ({ className, ...props }, ref) => (
    <SwitchPrimitive.Root
      ref={ref}
      className={cn(
        'inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border border-field-border bg-sunken transition-colors data-[state=checked]:border-accent data-[state=checked]:bg-accent disabled:opacity-50',
        focusRing,
        className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb className="pointer-events-none block size-4 translate-x-0.5 rounded-full bg-surface shadow-sm ring-1 ring-field-border transition-transform data-[state=checked]:translate-x-[17px]" />
    </SwitchPrimitive.Root>
  ),
);
Switch.displayName = 'Switch';

export function Checkbox({ className, ...props }: React.ComponentProps<'input'>) {
  return <input type="checkbox" className={cn('size-4 cursor-pointer rounded-[var(--lr-radius-sm)] accent-[var(--accent)]', focusRing, className)} {...props} />;
}

/** Campo com rótulo, ajuda e erro ligados por aria. */
export function Field({
  id,
  label,
  hint,
  error,
  children,
  className,
}: {
  id: string;
  label: React.ReactNode;
  hint?: React.ReactNode;
  error?: string | undefined;
  children: React.ReactElement<Record<string, unknown>>;
  className?: string;
}) {
  // A ajuda some quando há erro; o aria aponta só para o que está na tela.
  const describedBy = error ? `${id}-erro` : hint ? `${id}-ajuda` : undefined;
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Label htmlFor={id}>{label}</Label>
      {React.cloneElement(children, { id, 'aria-invalid': error ? true : undefined, 'aria-describedby': describedBy })}
      {hint && !error && (
        <p id={`${id}-ajuda`} className="text-[length:var(--lr-text-xs)] text-fg-muted">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${id}-erro`} role="alert" className="text-[length:var(--lr-text-xs)] font-medium text-danger">
          {error}
        </p>
      )}
    </div>
  );
}
